import React, { useState, useCallback } from 'react';
import { getLanguage } from '@deadpan/shared';
import { useTranslations } from '../hooks/useTranslations';

function parseOptions(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

interface DrawLotsProps {
  /** When true, used inside modal (no card wrapper, compact header) */
  embedded?: boolean;
}

export const DrawLots: React.FC<DrawLotsProps> = ({ embedded = false }) => {
  const t = useTranslations();
  const [inputText, setInputText] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const options = parseOptions(inputText);

  const handleDraw = useCallback(() => {
    const list = parseOptions(inputText);
    if (list.length === 0) {
      setError(t.ui.drawLotsNeedOptions);
      setResult(null);
      return;
    }
    setError(null);
    const index = Math.floor(Math.random() * list.length);
    setResult(list[index]);
  }, [inputText, t.ui.drawLotsNeedOptions]);

  const wrapperClass = embedded
    ? 'max-w-md'
    : 'bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 max-w-md w-full border border-white/20';

  return (
    <div className={wrapperClass}>
      <div className={embedded ? 'mb-3' : 'mb-4 sm:mb-6'}>
        {!embedded && (
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-2">
            {t.ui.drawLotsTitle}
          </h2>
        )}
        <p className="text-xs sm:text-sm text-gray-600">{t.ui.drawLotsDescription}</p>
      </div>
      <div className="space-y-4">
        <div>
          <label htmlFor="drawLotsOptions" className="block text-sm font-semibold text-gray-700 mb-2">
            {t.ui.drawLotsPlaceholder}
          </label>
          <textarea
            id="drawLotsOptions"
            value={inputText}
            onChange={(e) => {
              setInputText(e.target.value);
              setError(null);
              setResult(null);
            }}
            placeholder={t.ui.drawLotsPlaceholder}
            rows={5}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all outline-none resize-y min-h-[100px]"
          />
        </div>
        {error && (
          <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-3 rounded-xl">
            {error}
          </div>
        )}
        {result !== null && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
            <p className="text-xl sm:text-2xl font-bold text-emerald-700 break-words">{result}</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleDraw}
          disabled={options.length === 0}
          className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-emerald-700 hover:to-teal-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          🎲 {t.ui.drawLotsButton ?? (getLanguage() === 'zh' ? '抽签' : 'Draw')}
        </button>
      </div>
    </div>
  );
};
